import React, { useState } from 'react';
import { Eye, Calendar, AlertTriangle, CheckCircle, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { EmptyState } from '../common/EmptyState';

interface SymptomReport {
  id: string | number;
  patient_id?: string | number;
  patient_name: string;
  hospital_name?: string;
  hospital_id?: string | number;
  symptoms: string | string[];
  notes?: string;
  severity?: string;
  status?: 'escalated' | 'under_review' | 'resolved' | 'pending';
  date?: string;
  time?: string;
  timestamp?: string;
}

interface CoordinatorSymptomsTrackerProps {
  symptoms: SymptomReport[];
  hospitals: Array<{ id: string | number; name: string }>;
  onViewHistory?: (patient: SymptomReport) => void;
  onScheduleAppointment?: (patient: SymptomReport) => void;
  onEscalate?: (symptomId: string | number) => void;
  onResolve?: (symptomId: string | number) => void;
}

export function CoordinatorSymptomsTracker({
  symptoms = [],
  hospitals = [],
  onViewHistory,
  onScheduleAppointment,
  onEscalate,
  onResolve,
}: CoordinatorSymptomsTrackerProps) {
  const [selectedHospital, setSelectedHospital] = useState<string>('all');

  // Filter symptoms by hospital
  const filteredSymptoms = symptoms.filter(symptom => {
    if (selectedHospital === 'all') return true;
    return symptom.hospital_id?.toString() === selectedHospital;
  });

  // Calculate statistics
  const totalReports = filteredSymptoms.length;
  const escalated = filteredSymptoms.filter(s => s.status === 'escalated').length;
  const underReview = filteredSymptoms.filter(s => s.status === 'under_review').length;
  const resolved = filteredSymptoms.filter(s => s.status === 'resolved').length;

  // Parse symptoms array or string
  const parseSymptoms = (symptoms: string | string[]): string[] => {
    if (Array.isArray(symptoms)) return symptoms;
    if (typeof symptoms === 'string') {
      try {
        const parsed = JSON.parse(symptoms);
        return Array.isArray(parsed) ? parsed : [symptoms];
      } catch {
        return symptoms.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  // Format date and time
  const formatDateTime = (symptom: SymptomReport) => {
    if (symptom.date && symptom.time) {
      return `${symptom.date} at ${symptom.time}`;
    }
    if (symptom.timestamp) {
      const date = new Date(symptom.timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    return symptom.date || 'Date not available';
  };

  // Get severity color
  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
        return 'bg-red-100 text-red-600 border-red-200';
      case 'moderate':
        return 'bg-orange-100 text-[#F07D13] border-orange-200';
      case 'mild':
        return 'bg-blue-100 text-[#14CAF4] border-blue-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Get status color
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'escalated':
        return 'bg-red-100 text-red-600 border-red-200';
      case 'under_review':
        return 'bg-blue-100 text-[#14CAF4] border-blue-200';
      case 'resolved':
        return 'bg-green-100 text-green-600 border-green-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-primary text-2xl">Patient Symptoms Tracker</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage patient symptom reports
          </p>
        </div>
        
        {/* Hospital Filter */}
        <Select value={selectedHospital} onValueChange={setSelectedHospital}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="All Hospitals" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Hospitals</SelectItem>
            {hospitals.map(hospital => (
              <SelectItem key={hospital.id} value={hospital.id.toString()}>
                {hospital.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Total Reports</p>
            <p className="text-3xl text-primary">{totalReports}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Escalated</p>
            <p className="text-3xl text-red-500">{escalated}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#14CAF4]">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Under Review</p>
            <p className="text-3xl text-[#14CAF4]">{underReview}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Resolved</p>
            <p className="text-3xl text-green-500">{resolved}</p>
          </CardContent>
        </Card>
      </div>

      {/* Symptom Reports List */}
      <div className="space-y-4">
        {filteredSymptoms.length === 0 ? (
          <EmptyState
            icon={CheckCircle}
            title="No symptom reports"
            description={
              selectedHospital === 'all'
                ? 'No patient symptoms to display'
                : 'No symptoms reported for this hospital'
            }
          />
        ) : (
          filteredSymptoms.map((symptom) => {
            const symptomList = parseSymptoms(symptom.symptoms);
            const isEscalated = symptom.status === 'escalated';
            const isResolved = symptom.status === 'resolved';

            return (
              <Card key={symptom.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Patient Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-[#2762AC] flex items-center justify-center">
                        <span className="text-white">
                          {symptom.patient_name
                            .split(' ')
                            .map(n => n[0])
                            .join('')
                            .toUpperCase()}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 space-y-3">
                      {/* Patient Info */}
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                        <div>
                          <h3 className="text-primary">{symptom.patient_name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {symptom.hospital_name || 'Hospital not specified'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDateTime(symptom)}
                          </p>
                        </div>
                        
                        {/* Status Badges */}
                        <div className="flex gap-2">
                          {symptom.severity && (
                            <Badge variant="outline" className={getSeverityColor(symptom.severity)}>
                              {symptom.severity}
                            </Badge>
                          )}
                          {symptom.status && (
                            <Badge variant="outline" className={getStatusColor(symptom.status)}>
                              {symptom.status === 'under_review' ? 'Under Review' : symptom.status}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Reported Symptoms */}
                      <div>
                        <p className="text-sm mb-2">Reported Symptoms:</p>
                        <ul className="list-disc list-inside space-y-1">
                          {symptomList.map((s, idx) => (
                            <li key={idx} className="text-sm text-foreground">
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Additional Notes */}
                      {symptom.notes && (
                        <div className="bg-[#FEEBDA] border border-[#F07D13]/20 rounded-lg p-3">
                          <p className="text-sm">
                            <span className="text-muted-foreground">Additional Notes: </span>
                            <span className="text-foreground">{symptom.notes}</span>
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onViewHistory?.(symptom)}
                          className="border-[#2762AC] text-[#2762AC] hover:bg-[#2762AC] hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Patient History
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onScheduleAppointment?.(symptom)}
                          className="border-[#14CAF4] text-[#14CAF4] hover:bg-[#14CAF4] hover:text-white"
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Schedule Appointment
                        </Button>

                        {!isResolved && !isEscalated && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onEscalate?.(symptom.id)}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Escalate
                          </Button>
                        )}

                        {!isResolved && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onResolve?.(symptom.id)}
                            className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Info Card */}
      <Card className="bg-[#FEEBDA] border-[#F07D13]/20">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Lightbulb className="w-5 h-5 text-[#F07D13] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-[#F07D13] mb-1">Coordinator Dashboard</h4>
              <p className="text-sm text-foreground">
                Manage patients across multiple hospitals, review details and symptoms, 
                and schedule appointments with doctors.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
